"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.returnDataOrThrow = void 0;
exports.generateHttpClientLogMessage = generateHttpClientLogMessage;
exports.getApiEndpoint = getApiEndpoint;
/* eslint-disable @typescript-eslint/no-explicit-any */
const rxjs_1 = require("rxjs");
const uuid_1 = require("uuid");
const AppSettings_1 = __importDefault(require("./AppSettings"));
const FetchRetry_1 = __importDefault(require("./FetchRetry"));
const Helpers_1 = require("./Helpers");
const IframeDetector_1 = require("./IframeDetector");
const Log = __importStar(require("./Log"));
const ErrorFiltering_1 = require("./errors/ErrorFiltering");
/**
 * HttpClient issue grouping
 * =========================
 * Different properties can be used to control issue grouping. For HttpClient
 * issues, we want to group them by the URL of the page where the error occurred,
 * the endpoint that generated the error, and the type of the error/issue.
 */
const SentryErrorGroup = [
    "{{ tags.route.path }}",
    "{{ tags.api.endpoint }}",
    "{{ tags.api.method }}",
    "{{ message }}",
];
/**
 * The common HttpClient for interacting with all of our backend APIs. This
 * module exists to abstract the following concerns
 *
 * - Safety/error-handling
 * All methods make use of error handling, capturing errors, and logging
 * appropriately. This means that all methods return defined types, and none
 * throw. This simplifies the concerns that calling code has to deal with.
 *
 * - Automatic retries
 * There is some retry logic baked into all methods. There will be up to 3
 * times on any network error, if the server did not send a valid response back
 * (i.e. status code 500)
 *
 * - Sentry logging
 * All logging to Sentry is consistent, for maximum usefulness.
 */
class HttpClient {
    constructor() {
        this._unauthorizedSubject = new rxjs_1.Subject();
    }
    /**
     * An observable to propogate the occurrence of HTTP 401 responses out to subscribers
     *
     * @privateRemarks
     *
     * The thinking here _seems_ to be that a 401 response on any API call indicates that
     * the user does not have the requisite permissions, and therefore we may wish to take
     * further action off the back of that. That seems a bit of a stretch to me, but this
     * is what we can see happening in the block beginning on line:362
     *
     *     ```
     *     if (response.status === 401) {
     *          if (shouldNotifyIfUnauthorized) {
     *              this._unauthorizedSubject.next();
     *          }
     *          return failJson(response.status, null);
     *     }
     *     ```
     *
     * This observable is exposed via `FlemingApiClient.unauthorizedObservable`, and is
     * subscribed to in `registerUnauthorizedHandling()` in src/index.tsx
     *
     */
    get unauthorizedObservable() {
        return this._unauthorizedSubject;
    }
    /**
     * Performs GET request asynchronously, handling errors safely and returns a JsonResult.
     */
    getReturnJsonSafeAsync(url, shouldNotifyIfUnauthorized = true) {
        return this.doRequestReturnJsonSafeAsync(url, getJsonOptions, shouldNotifyIfUnauthorized);
    }
    /**
     * Performs GET request asynchronously, handling errors safely and returns an IWrappedResult.
     */
    getReturnSafeAsync(url, shouldNotifyIfUnauthorized = true) {
        return this.doRequestNoReturnSafeAsync(url, getOptions, shouldNotifyIfUnauthorized);
    }
    /**
     * Performs POST request with JSON payload asynchronously, handling errors safely and returns a JsonResult.
     */
    postJsonReturnJsonSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestReturnJsonSafeAsync(url, postOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs POST request with JSON payload asynchronously, handling errors safely and returns an IWrappedResult.
     */
    postJsonReturnSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestNoReturnSafeAsync(url, postOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs POST request with no payload asynchronously, handling errors safely and returns a boolean indicating success.
     */
    postSafeAsync(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, shouldNotifyIfUnauthorized = true) {
            const success = (yield this.doRequestSafeAsync(url, postOptions, shouldNotifyIfUnauthorized, () => Promise.resolve())).success;
            return { success };
        });
    }
    /**
     * Performs POST request with no payload asynchronously, handling errors safely and returns an IWrappedResult.
     */
    postReturnSafeAsync(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, shouldNotifyIfUnauthorized = true) {
            return this.doRequestReturnJsonSafeAsync(url, postOptions, shouldNotifyIfUnauthorized);
        });
    }
    /**
     * Performs POST request with a file as the payload, handling errors safely and returns a boolean indicating success.
     */
    postFileSafeAsync(url_1, data_1) {
        return __awaiter(this, arguments, void 0, function* (url, data, shouldNotifyIfUnauthorized = true) {
            return this.doRequestSafeAsync(url, postFileOptions(data), shouldNotifyIfUnauthorized, (response) => __awaiter(this, void 0, void 0, function* () { return yield response.json(); }));
        });
    }
    /**
     * Performs PUT request with JSON payload asynchronously, handling errors safely and returns an IWrappedResult.
     */
    putJsonReturnSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestNoReturnSafeAsync(url, putOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs DELETE request asynchronously, handling errors safely and returns a JsonResult.
     */
    deleteReturnJsonSafeAsync(url, shouldNotifyIfUnauthorized = true) {
        return this.doRequestSafeAsync(url, deleteOptions, shouldNotifyIfUnauthorized, () => Promise.resolve());
    }
    /**
     * Performs DELETE request with JSON payload asynchronously, handling errors safely and returns a JsonResult.
     */
    deleteJsonReturnJsonSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestReturnJsonSafeAsync(url, deleteOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    deleteJsonReturnSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestNoReturnSafeAsync(url, deleteOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs PATCH request with JSON payload asynchronously, handling errors safely and returns a JsonResult.
     */
    patchJsonReturnJsonSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestReturnJsonSafeAsync(url, patchOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs PUT request with JSON payload asynchronously, handling errors safely and returns a JsonResult.
     */
    putJsonReturnJsonSafeAsync(url, body, shouldNotifyIfUnauthorized = true) {
        return this.doRequestReturnJsonSafeAsync(url, putOptionsWithBody(body), shouldNotifyIfUnauthorized);
    }
    /**
     * Performs GET request asynchronously, handling errors safely and returns HTML.
     */
    getReturnHtmlSafeAsync(url_1) {
        return __awaiter(this, arguments, void 0, function* (url, shouldNotifyIfUnauthorized = true, configureInit) {
            const init = configureInit(getOptions);
            return this.doRequestReturnHtmlSafeAsync(url, init, shouldNotifyIfUnauthorized);
        });
    }
    doRequestReturnJsonSafeAsync(url, init, shouldNotifyIfUnauthorized) {
        return this.doRequestSafeAsync(url, init, shouldNotifyIfUnauthorized, (response) => __awaiter(this, void 0, void 0, function* () { return yield response.json(); }));
    }
    doRequestNoReturnSafeAsync(url, init, shouldNotifyIfUnauthorized) {
        return this.doRequestSafeAsync(url, init, shouldNotifyIfUnauthorized, (_a) => __awaiter(this, [_a], void 0, function* ({}) { }));
    }
    doRequestReturnHtmlSafeAsync(url, init, shouldNotifyIfUnauthorized) {
        return this.doRequestSafeAsync(url, init, shouldNotifyIfUnauthorized, (response) => __awaiter(this, void 0, void 0, function* () { return yield response.text(); }));
    }
    /**
     * The common underlying method that makes all network requests and handles
     * all related concerns (retries, errors, logging etc).
     *
     * @privateRemarks
     *
     * All HttpClient methods fall through to call this one. This is the one that
     * rules them all. If we wish to change any underlying behaviour, this is the
     * place to do it.
     *
     * Some details of the implementation
     *
     * - additional common headers are added to all requests (e.g. csrf)
     * - HTTP 302 response with a url will cause a page redirect 👀
     * - HTTP 401 responses might bubble, but they do not log to Sentry
     * - HTTP 400|405|409 responses are explicitly handled and logged to Sentry
     *
     * Summary of the logs this method will produce
     *
     * - "[HttpClient] Failed to fetch"           unable to fulfil/complete a request
     * - "[HttpClient] Failed to map response"    error calling the mapResponse callback argument
     * - "[HttpClient] failure"                   HTTP 400|405|409 responses
     * - "[HttpClient] Request failed"            fallback catch-all error
     */
    doRequestSafeAsync(url, requestDetails, shouldNotifyIfUnauthorized, mapResponse) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            let response;
            let httpMethod = "Unknown";
            const { endpoint, query, params, description } = parseApiUrl(url);
            const additionalLogData = url.additionalLogData || {};
            const correlationId = (0, uuid_1.v4)();
            const sentryContext = [
                {
                    name: "Additional data",
                    data: additionalLogData,
                },
            ];
            const sentryTags = createSentryTags(endpoint, correlationId, query, params, httpMethod);
            // IMPORTANT - this is where new approach to supplying URLs converges
            // to the existing/old. do not remove this piece of code until the type
            // HttpClientUrl no longer exists
            if ("schema" in url && url.schema === "httpclient.url.v2") {
                url.url = url.urlFinal;
            }
            httpMethod = (_a = requestDetails === null || requestDetails === void 0 ? void 0 : requestDetails.method) !== null && _a !== void 0 ? _a : httpMethod;
            //We need to take a copy here to ensure we don't modify what is passed in
            const headers = new Headers(Object.assign({}, requestDetails === null || requestDetails === void 0 ? void 0 : requestDetails.headers));
            if (headers.has("AccurxCorrelationId") ||
                headers.has("Iframe") ||
                headers.has("X-CSRF-TOKEN") ||
                headers.has("AccurxPrNumber")) {
                Log.error("Original headers have been mutated rather than copied further down");
            }
            const csrfToken = (0, Helpers_1.getCookie)("CSRF-TOKEN");
            const accurxPrNumber = AppSettings_1.default.accurxPrNumber;
            headers.set("Iframe", (0, IframeDetector_1.InIframe)().toString());
            headers.set("AccurxCorrelationId", correlationId);
            if (csrfToken) {
                /**
                 * Add a X-CSRF-TOKEN header to all calls to Prevent Cross-Site Request Forgery (XSRF/CSRF) attacks
                 * https://docs.microsoft.com/en-us/aspnet/core/security/anti-request-forgery?view=aspnetcore-5.0
                 */
                headers.set("X-CSRF-TOKEN", csrfToken);
            }
            if (accurxPrNumber) {
                headers.set("AccurxPrNumber", accurxPrNumber);
            }
            const apiClientName = AppSettings_1.default.getWebClientAppName();
            const apiClientVersion = AppSettings_1.default.getWebClientAppVersion();
            if (apiClientName && apiClientVersion) {
                headers.set("app-name", apiClientName);
                headers.set("app-version", apiClientVersion);
            }
            sentryTags["api.method"] = httpMethod;
            sentryContext.push({
                name: "Api Request",
                data: {
                    url: url.url,
                    method: httpMethod,
                    headers: headers,
                    referrer: requestDetails === null || requestDetails === void 0 ? void 0 : requestDetails.referrer,
                },
            });
            try {
                // capture the request being initiated
                Log.info(`HttpClient request starting`, {
                    tags: sentryTags,
                });
                response = yield FetchRetry_1.default.fetchRetry(url, Object.assign(Object.assign({}, requestDetails), { headers: headers }));
            }
            catch (error) {
                const ex = error;
                const errorMessage = generateHttpClientLogMessage({
                    message: "Failed to fetch",
                    endpoint: description,
                    method: httpMethod,
                    details: [
                        `Error: ${ex}`,
                        `${ex.statusCode ? `Status: ${ex.statusCode}` : ""}`,
                    ],
                });
                logFailedRequest(errorMessage, {
                    originalException: error,
                    tags: sentryTags,
                    context: sentryContext,
                    errorGroup: SentryErrorGroup,
                });
                return failJson(null, null);
            }
            const status = response.status;
            sentryTags["api.statusCode"] = status;
            // Handle unauthorized (ie: 401)
            if (status === 401) {
                // controls whether to propogate the occurrence of a HTTP 401
                // reponse out to any subscribers elsewhere. this arguments value
                // defaults to true for most of the public HttpClient methods.
                if (shouldNotifyIfUnauthorized) {
                    this._unauthorizedSubject.next();
                }
                return failJson(status, null);
            }
            else if (!response.ok) {
                const accurxApiError = yield (0, Helpers_1.getAccurxErrorMessage)(response);
                sentryTags["api.accurxErrorMessage"] =
                    accurxApiError !== null && accurxApiError !== void 0 ? accurxApiError : "no message returned";
                try {
                    const errorMessage = generateHttpClientLogMessage({
                        message: "failure",
                        endpoint: description,
                        method: httpMethod,
                        details: [`Code: ${status}`],
                    });
                    const responseContext = {
                        name: "Api Response",
                        data: {
                            code: status,
                            headers: response.headers,
                        },
                    };
                    logClientErrorResponse(errorMessage, endpoint, status, accurxApiError, {
                        tags: sentryTags,
                        context: [...sentryContext, responseContext],
                        errorGroup: SentryErrorGroup,
                    });
                    return failJson(status, accurxApiError);
                }
                catch (error) {
                    if (error instanceof Error) {
                        return failJson(status, error.message);
                    }
                    else {
                        return failJson(status, null);
                    }
                }
            }
            // Handle non-error response
            let mappedResponse;
            try {
                mappedResponse = yield mapResponse(response);
                // capture the successful request completing
                Log.info(`HttpClient request succeeded`, {
                    tags: sentryTags,
                });
                return successJson(status, mappedResponse);
            }
            catch (error) {
                const errorMessage = generateHttpClientLogMessage({
                    message: "Failed to map response",
                    endpoint: description,
                    method: httpMethod,
                    details: [`Error: ${error}`],
                });
                logFailedRequest(errorMessage, {
                    originalException: error,
                    tags: sentryTags,
                    context: sentryContext,
                    errorGroup: SentryErrorGroup,
                });
                return failJson(null, null);
            }
        });
    }
}
const getOptions = {
    method: "GET",
};
const getJsonOptions = Object.assign(Object.assign({}, getOptions), { headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Cache: "no-cache",
    }, credentials: "same-origin" });
const postOptions = {
    method: "POST",
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Cache: "no-cache",
    },
    credentials: "same-origin",
};
const deleteOptions = {
    method: "DELETE",
    headers: {
        Cache: "no-cache",
        Pragma: "no-cache",
    },
    credentials: "same-origin",
};
const putOptionsWithBody = (bodyData) => ({
    method: "PUT",
    body: JSON.stringify(bodyData),
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Cache: "no-cache",
    },
    credentials: "same-origin",
});
const postFileOptions = (data) => {
    return {
        method: "POST",
        body: data,
        headers: {
            // Don't set Content-Type when posting a file
            Cache: "no-cache",
        },
        credentials: "same-origin",
    };
};
const postOptionsWithBody = (bodyData) => (Object.assign(Object.assign({}, postOptions), { body: JSON.stringify(bodyData) }));
const deleteOptionsWithBody = (bodyData) => ({
    method: "DELETE",
    body: JSON.stringify(bodyData),
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Cache: "no-cache",
    },
    credentials: "same-origin",
});
const patchOptionsWithBody = (bodyData) => ({
    method: "PATCH",
    body: JSON.stringify(bodyData),
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Cache: "no-cache",
    },
    credentials: "same-origin",
});
const failJson = (statusCode, error) => {
    return {
        error: error,
        success: false,
        result: null,
        statusCode: statusCode,
    };
};
const successJson = (statusCode, item) => {
    return {
        error: null,
        success: true,
        result: item,
        statusCode: statusCode,
    };
};
function logFailedRequest(errorMessage, options) {
    /**
     * The navigator object is a global property that contains
     * information about the browser.
     *
     * See https://developer.mozilla.org/en-US/docs/Web/API/Navigator
     */
    if (window.navigator.onLine === false) {
        Log.info(errorMessage, options);
    }
    else {
        // The exceptions from fetch don't contain detailed information about the request
        if ((0, Helpers_1.isNetworkIssue)(options.originalException)) {
            options.tags["api.errorType"] = "Network Issue";
            Log.warn(errorMessage, options);
        }
        else {
            Log.error(errorMessage, options);
        }
    }
}
function logClientErrorResponse(logMessage, endpoint, status, apiErrorMessage, options) {
    const shouldLogError = status > 399 && status < 500 && [401, 404].includes(status) === false; // exclude these for now
    const isExpectedResponse = (0, ErrorFiltering_1.isExpectedApiResponse)({
        apiUrl: endpoint,
        apiStatus: status,
        apiResponse: apiErrorMessage || "",
    });
    if (shouldLogError) {
        if (isExpectedResponse) {
            options.tags["api.errorType"] = "Expected Response";
            Log.warn(logMessage, options);
        }
        else {
            Log.error(logMessage, options);
        }
    }
    else {
        Log.info(logMessage, options);
    }
}
/**
 * Generate error messages for issues logged to Sentry.
 *
 * This controls the overall format of error messages. Error details are pushed
 * into the message (e.g. HTTP method, status code etc) as we sent issues to
 * Sentry using Log.errorWithExtras() which doesn't expose other Sentry
 * APIs (e.g. tags). The structure and format of these issues will change, so
 * having a common implementation is a useful first step
 *
 * @param  {LogMessageDetails} details - all the contituent pieces of info that go into creating a Sentry message
 * @return {string}
 */
function generateHttpClientLogMessage({ message, endpoint, method, details, }) {
    let logMessage = `[HttpClient] ${message}. Method: ${method}. Request: ${endpoint}. `;
    if (details) {
        logMessage += ` ${details.join(". ")}`.trim();
    }
    if (logMessage[logMessage.length - 1] !== ".") {
        logMessage += ".";
    }
    return logMessage;
}
/**
 * Parse incoming API URL objects
 *
 * API requests that want to benefit from improved logging need to supply the
 * URL using the newer HttpClientApiEndpoint type. In order to remain backwards
 * compatable, this function handles the parsing of both old and new schemas,
 * to return back the relevant data needed.
 *
 * @param  {HttpClientUrl  | HttpClientApiEndpoint} url - the API URL in one of the supported structures. Favour using HttpClientApiEndpoint
 * @return {ParsedApiEndpoint}
 */
function parseApiUrl(url) {
    var _a, _b;
    // the existing schema (HttpClientUrl)
    if ("urlLogDescription" in url) {
        const [endpoint, query] = url.url.split("?");
        return {
            endpoint,
            query,
            params: {},
            description: url.urlLogDescription,
        };
    }
    // the new schema (HttpClientApiEndpoint)
    if ("schema" in url && url.schema === "httpclient.url.v2") {
        return {
            endpoint: url.url,
            query: (_a = url.query) !== null && _a !== void 0 ? _a : "",
            params: (_b = url.parameters) !== null && _b !== void 0 ? _b : {},
            description: url.url,
        };
    }
    return {
        endpoint: "",
        query: "",
        params: {},
        description: "",
    };
}
/**
 * Create the Sentry tags object used for the duration of the current request
 *
 * @param {string} endpoint - the API endpoint (i.e. path or URL)
 * @param {string} correlationId - the accurx correlation Id, a uuid set once per request and plumbed through to backend logs too
 * @param {string} query - the URL query string
 * @param {ApiParams} params - the request body
 * @param {string} httpMethod - the HTTP verb for the request
 * @return {SentryHttpClientTags}
 */
function createSentryTags(endpoint, correlationId, query, params, httpMethod) {
    const sentryTags = {
        logger: "HttpClient",
        "api.correlationId": correlationId,
        "api.endpoint": endpoint,
        "api.method": httpMethod,
    };
    if (query) {
        sentryTags["api.query"] = query;
    }
    for (const key in params) {
        if (params[key]) {
            sentryTags[`api.params.${key}`] = params[key];
        }
    }
    return sentryTags;
}
/**
 * generate the HttpClient API endpoint that you wish to send a request to
 *
 * @param {ApiEndpoint<P>} endpoint
 * the /api/* path you wish to send a request to. any part of the path that is a
 * parameter should be indicated by using /:paramName/ syntax
 */
function getApiEndpoint(endpoint) {
    var _a;
    const normalisedPath = `/api/${endpoint.path
        .replace(/^\//, "")
        .replace(/^api\/?/, "")}`;
    const apiEndpoint = {
        /** property that holds the parameterised API endpoint e.g. "/api/organisation/:orgId" */
        url: normalisedPath,
        /** property that holds the final merged API URL e.g. "/api/organisation/123?filter=all" */
        urlFinal: normalisedPath,
        schema: "httpclient.url.v2",
    };
    if ("params" in endpoint) {
        apiEndpoint.parameters = endpoint.params;
        for (const key in endpoint.params) {
            const mergedPath = apiEndpoint.urlFinal.replace(`:${key}`, endpoint.params[key]);
            apiEndpoint.urlFinal = mergedPath;
        }
    }
    if ("query" in endpoint) {
        apiEndpoint.query = endpoint.query;
        apiEndpoint.urlFinal += (_a = endpoint.query) !== null && _a !== void 0 ? _a : "";
    }
    return apiEndpoint;
}
/**
 * HTTP Client always returns a successful data payload - even when
 * the HTTP request failed.
 *
 * This utility reverses that process and rethrows any error so that
 * React Query can catch errors and handle them.
 *
 * Example usage :
 *     return httpClient
 *      .getReturnJsonSafeAsync(
 *           getApiEndpoint({
 *               path: ...,
 *               query: ...,
 *           }),
 *       )
 *       .then(returnDataOrThrow)
 *       .then(...handle successful response)
 *
 * @param response the wrapped response that HttpClient provides
 * @returns the successful data inside the wrapped response
 * @deprecated Use httpClient2 instead.
 */
const returnDataOrThrow = (response) => {
    var _a;
    if (response.success === true && response.result !== null) {
        return response.result;
    }
    throw new Error((_a = response.error) !== null && _a !== void 0 ? _a : "Request failed to fetch data");
};
exports.returnDataOrThrow = returnDataOrThrow;
const httpClient = new HttpClient();
/**
 * @deprecated Use httpClient2 instead.
 */
exports.default = httpClient;
/* eslint-enable @typescript-eslint/no-explicit-any */
//# sourceMappingURL=HttpClient.js.map